import { Inject, Injectable } from '@angular/core';
import { BaseService } from '@app/core/services/base/base.service';
import { APP_CONFIG, AppConfig } from '@app/app-config.module';
import { TranslateService } from '@ngx-translate/core';
import { ContainedPrimaryButton, ExportFormat, ImageFormat, OutlinedSecondaryButton } from '@assets/vendor/photoeditorsdk';
import { DrI18nService } from '@app/core/services/i18n/i18n.service';
import styled from 'styled-components';

@Injectable({
    providedIn: 'root'
})
export class PhotoEditorConfigService extends BaseService {

    private license = '{"owner":"OnREX GmbH","version":"2.1","enterprise_license":false,"available_actions":["magic","filter","transform","sticker","text","adjustments","brush","focus","frames","camera"],"features":["adjustment","filter","focus","overlay","transform","text","sticker","frame","brush","camera","library","export"],"platform":"HTML5","app_identifiers":["*.photoeditorsdk.com","dynarex.local","dynarex.ionic","localhost","*.onrex.de"],"api_token":"IsxPvjhprseR47uHiqXKLA","domains":["https://api.photoeditorsdk.com"],"issued_at":1540455097,"expires_at":null,"signature":"pGuF0GEaO9BVErvgfRD4igNQ2UpI0veHtcJZob5a4ZGui+nYE4DehkHoX6NhSHDKH9s3AgLDSZKejlW4axD4jgqbtdnqp8eIL3P/ulBVE0WgBOeWySeAO4fhcZz3LJjpD0EQjA+W1kedzJ3xkl9T3GD0YzljmcfKu8/fd2zdxOOWY7PrmSUcdiFmQxnfwCy4eBnpAlDe4dAAdosRJGE6DAh98Xe8iSZu7hqQisT/KjPCxj7oL8yoACgKbRHggaA7iCpaJ4eSD2XpVseTuI5AdogQXhPYNygUrUAa8BKGxi3rjRcSh9k1jz0WpUPfbsUAkeB8tWp8mfD8KCTgMhM9HcMm3y9gSdiwvr110havuhu0VrJ8UDiVchDKLUwATGsCDq/LSvHZRQCZtI8Fj5XbbaxQQ4B8V88OM7KY5+1n+PHfQAV/vQpF0DyLieW0s89v2QbqhrdGYgI87K5rZUjhn11agMBm/D7xMnSrW0xUhioGxc7jnCFd7xPTrXtF5gWI91WwnHLIYnpDh1sOQTbyWqFE6lnjpP3jw1WTXBHDdMfMjW3qx9BXd+T4J4h0zH0qRre8noi75Aiw1NTDUdFMP9EMfjd0gcTtM5aKg8rayuY/4NTg5c61XiLKANUAKBv4C84eH2Tt+U4txnBtISNz1SGYcJ7NBUxpeyJLCgMa+Aw="}';

    protected config: any;

    constructor(protected translateService: TranslateService,
                protected drI18nService: DrI18nService,
                @Inject(APP_CONFIG) protected appConfig: AppConfig) {
        super();
    }

    protected buildConfig(): void {
        if (this.appConfig?.photoEditor) {
            this.config = Object.assign({}, this.appConfig?.photoEditor);

            this.config.license = this.license;
            this.config.language = DrI18nService.getActiveLocaleInformation().shortName;

            // Generating translated sticker categories with stickers
            if (this.config.smpConfigBuilderParams?.stickerColors) {
                this.config.smpConfigBuilderParams.stickerColors.forEach(color => {
                    const categoryIdentifier = `forms-${color.name}`;

                    const newCategory = {
                        identifier: categoryIdentifier,
                        name: this.translateService.instant(color.categoryName),
                        thumbnailURI: `${this.config.smpConfigBuilderParams.stickerPaths.category}${categoryIdentifier}@2x.png`, // path to the sticker, relative to the sticker asset directory
                        items: []
                    };

                    if (this.config.smpConfigBuilderParams?.customStickers) {
                        const PLACEHOLDER = 'COLOR_PLACEHOLDER';
                        this.config.smpConfigBuilderParams.customStickers.forEach(sticker => {
                            const coloredSticker = Object.assign({}, sticker);
                            const identifier = coloredSticker.identifier.replace(PLACEHOLDER, color.name);

                            coloredSticker.identifier = identifier;
                            coloredSticker.thumbnailURI = `${this.config.smpConfigBuilderParams.stickerPaths.thumb}${identifier}.png`; // path to the thumbnail, relative to the sticker asset directory
                            coloredSticker.stickerURI = `${this.config.smpConfigBuilderParams.stickerPaths.base}${identifier}.png`; // path to the sticker, relative to the sticker asset directory


                            newCategory.items.push(coloredSticker);
                        });
                    }

                    this.config.sticker.categories.push(newCategory);
                });
            }

            // Setting colors for text, background and brush
            if (this.config.smpConfigBuilderParams.colors) {
                this.config.text.backgroundColors = this.config.smpConfigBuilderParams.colors.concat(this.config.text.backgroundColors);
                this.config.text.colors = this.config.smpConfigBuilderParams.colors.concat(this.config.text.colors);
                this.config.brush.colors = this.config.smpConfigBuilderParams.colors.concat(this.config.brush.colors);
            }

            // Update buttons
             const ExportButton = styled(ContainedPrimaryButton)`
                color: #333333;
                background: #bfcf4f;
                border-color: #bfcf4f;
            `;

            const CloseButton = styled(OutlinedSecondaryButton)`
                display: none;
            `;

            this.config.custom = {
                components: {
                    buttons: {
                        mainCanvasActionExport: ExportButton,
                        mainCanvasActionClose: CloseButton,
                    }
                }
            };

            // Export
            this.config.export = {
                image: {
                    enableDownload: false,
                    format: ImageFormat.JPEG,
                    exportType: ExportFormat.DATA_URL
                }
            };

            // Cleanup builder params from the config object
            delete this.config.smpConfigBuilderParams;
        }
    }

    public getConfig(): any {
        if (!this.config) {
            this.buildConfig();
        }

        return this.config;
    }
}
